/**
 * Customers Cache Service
 * Manages offline caching and real-time sync of customers data
 */

import { subscribeToCollection, getDocuments } from '../db_calls';
import { getCollectionPath } from '../utils/dbPaths';
import { useCustomer } from '../stores';
import { isOnline } from './networkStatus';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize customers cache system
 * Sets up real-time listener and loads from cache if offline
 * @returns {Function} Unsubscribe function
 */
export const initializeCustomersCache = () => {
  // Firestore persistent cache handles offline data automatically

  // Set up real-time subscription with tenant/store path
  const collectionPath = getCollectionPath('customers');
  const unsubscribe = subscribeToCollection(
    collectionPath,
    (customers, error) => {
      if (error) {
        console.error('❌ Error in customers subscription:', error);
        return;
      }

      console.log(`👥 Received ${customers.length} customer(s) from Firestore`);
      
      // Add computed fullName to each customer
      const customersWithFullName = customers.map((customer) => ({
        ...customer,
        fullName: `${customer.firstName || ""} ${customer.lastName || ""}`.trim(),
      }));
      
      // Update Zustand store with the latest customers
      const setCustomers = useCustomer.getState().setCustomers;
      setCustomers(customersWithFullName);
      
      console.log('✅ Customers cache updated successfully');
    },
    {
      orderBy: {
        field: 'createdAt',
        direction: 'desc',
      },
    }
  );

  return unsubscribe;
};

/**
 * Get all customers from cache (synchronous)
 * @returns {Array} Array of customer objects
 */
export const getCustomersFromCache = () => {
  try {
    const customers = useCustomer.getState().customers;
    return customers;
  } catch (error) {
    console.error('❌ Error reading customers from cache:', error);
    return [];
  }
};

/**
 * Get a single customer by ID from cache
 * @param {string} customerId - Customer document ID
 * @returns {Object|null} Customer object or null if not found
 */
export const getCustomerFromCache = (customerId) => {
  const customers = getCustomersFromCache();
  const customer = customers.find(c => c.id === customerId);
  
  if (customer) {
    console.log(`📦 Found customer ${customerId} in cache`);
  } else {
    console.log(`⚠️ Customer ${customerId} not found in cache`);
  }
  
  return customer || null;
};

/**
 * Get customer by phone number from cache
 * @param {string} phone - Customer phone number
 * @returns {Object|null} Customer object or null if not found
 */
export const getCustomerByPhoneFromCache = (phone) => {
  const customers = getCustomersFromCache();
  const customer = customers.find(c => c.phone === phone);
  
  if (customer) {
    console.log(`📦 Found customer with phone ${phone} in cache`);
  } else {
    console.log(`⚠️ Customer with phone ${phone} not found in cache`);
  }
  
  return customer || null;
};

/**
 * Search customers by name from cache
 * @param {string} firstName - First name to search
 * @param {string} lastName - Last name to search
 * @returns {Array} Array of matching customer objects
 */
export const searchCustomersByNameFromCache = (firstName, lastName) => {
  const customers = getCustomersFromCache();
  
  return customers.filter((customer) => {
    const firstNameMatch = !firstName || 
      customer.firstName?.toLowerCase().includes(firstName.toLowerCase());
    const lastNameMatch = !lastName || 
      customer.lastName?.toLowerCase().includes(lastName.toLowerCase());
    
    return firstNameMatch && lastNameMatch;
  });
};

// localStorage caching functions removed - using Firestore persistent cache only

/**
 * Force refresh customers from server
 * @returns {Promise<Array>} Array of customers
 */
export const refreshCustomersCache = async () => {
  console.log('🔄 Forcing customers cache refresh from server...');
  
  try {
    const collectionPath = getCollectionPath('customers');
    const customers = await getDocuments(collectionPath, {
      orderBy: {
        field: 'createdAt',
        direction: 'desc',
      },
    });
    
    // Add computed fullName to each customer
    const customersWithFullName = customers.map((customer) => ({
      ...customer,
      fullName: `${customer.firstName || ""} ${customer.lastName || ""}`.trim(),
    }));
    
    // Update Zustand store
    const setCustomers = useCustomer.getState().setCustomers;
    setCustomers(customersWithFullName);
    
    console.log(`✅ Customers cache refreshed: ${customers.length} customer(s)`);
    return customersWithFullName;
  } catch (error) {
    console.error('❌ Error refreshing customers cache:', error);
    // Return cached data as fallback
    return getCustomersFromCache();
  }
};

/**
 * Update a single customer in cache
 * @param {string} customerId - Customer document ID
 * @param {Object} updates - Fields to update
 */
export const updateCustomerInCache = (customerId, updates) => {
  try {
    const customers = getCustomersFromCache();
    const index = customers.findIndex(c => c.id === customerId);
    
    if (index !== -1) {
      const updatedCustomers = [...customers];
      updatedCustomers[index] = { 
        ...updatedCustomers[index], 
        ...updates,
        fullName: `${updates.firstName || updatedCustomers[index].firstName || ""} ${updates.lastName || updatedCustomers[index].lastName || ""}`.trim()
      };
      
      // Update Zustand store
      const setCustomers = useCustomer.getState().setCustomers;
      setCustomers(updatedCustomers);
      
      console.log(`✅ Updated customer ${customerId} in cache`);
    } else {
      console.log(`⚠️ Customer ${customerId} not found in cache for update`);
    }
  } catch (error) {
    console.error('❌ Error updating customer in cache:', error);
  }
};

/**
 * Add a new customer to cache
 * @param {Object} customer - Customer object
 */
export const addCustomerToCache = (customer) => {
  try {
    const customers = getCustomersFromCache();
    
    // Check if customer already exists
    const existingIndex = customers.findIndex(c => c.id === customer.id);
    
    // Add fullName
    const customerWithFullName = {
      ...customer,
      fullName: `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
    };
    
    if (existingIndex !== -1) {
      // Update existing customer
      customers[existingIndex] = customerWithFullName;
      console.log(`✅ Updated existing customer ${customer.id} in cache`);
    } else {
      // Add new customer at the beginning (most recent first)
      customers.unshift(customerWithFullName);
      console.log(`✅ Added new customer ${customer.id} to cache`);
    }
    
    // Update Zustand store
    const setCustomers = useCustomer.getState().setCustomers;
    setCustomers(customers);
  } catch (error) {
    console.error('❌ Error adding customer to cache:', error);
  }
};

/**
 * Remove a customer from cache
 * @param {string} customerId - Customer document ID
 */
export const removeCustomerFromCache = (customerId) => {
  try {
    const customers = getCustomersFromCache();
    const filtered = customers.filter(c => c.id !== customerId);
    
    if (filtered.length < customers.length) {
      // Update Zustand store
      const setCustomers = useCustomer.getState().setCustomers;
      setCustomers(filtered);
      
      console.log(`✅ Removed customer ${customerId} from cache`);
    } else {
      console.log(`⚠️ Customer ${customerId} not found in cache for removal`);
    }
  } catch (error) {
    console.error('❌ Error removing customer from cache:', error);
  }
};

/**
 * Clear all customers from cache
 */
export const clearCustomersCache = () => {
  try {
    // Clear Zustand store
    useCustomer.getState().clearCustomers();
  } catch (error) {
    console.error('❌ Error clearing customers cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getCustomersCacheStats = () => {
  const customers = getCustomersFromCache();
  
  return {
    count: customers.length,
    lastUpdated: null, // No longer tracking timestamp - using Firestore cache
    averagePhoneNumbers: customers.filter(c => c.phone).length,
    averageEmails: customers.filter(c => c.email).length,
  };
};

